import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  History,
  LogOut,
  ChevronLeft,
  Download,
  Trash2,
  Phone,
  Mail,
  FileSpreadsheet,
  Loader2,
  RefreshCw,
  Filter,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HistoryPage = ({ user }) => {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [clearDialogOpen, setClearDialogOpen] = useState(false);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== "all") {
        params.append("input_type", filter);
      }

      const response = await fetch(`${API}/history?${params.toString()}`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setHistory(data.items);
      }
    } catch (error) {
      console.error("Error fetching history:", error);
      toast.error("Error al cargar historial");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/stats`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
    fetchStats();
  }, [fetchHistory, fetchStats]);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await fetch(`${API}/history/export?format=${format}`, {
        credentials: "include",
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `verification_history.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success(`Exportado como ${format.toUpperCase()}`);
      }
    } catch (error) {
      toast.error("Error al exportar");
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${API}/history/${id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        setHistory(history.filter((item) => item.id !== id));
        toast.success("Eliminado");
        fetchStats();
      }
    } catch (error) {
      toast.error("Error al eliminar");
      console.error(error);
    }
  };

  const handleClearAll = async () => {
    try {
      const response = await fetch(`${API}/history`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        setHistory([]);
        toast.success("Historial eliminado");
        fetchStats();
        setClearDialogOpen(false);
      }
    } catch (error) {
      toast.error("Error al limpiar historial");
      console.error(error);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "phone":
        return <Phone className="w-4 h-4" />;
      case "email":
        return <Mail className="w-4 h-4" />;
      case "bulk":
        return <FileSpreadsheet className="w-4 h-4" />;
      default:
        return <ShieldCheck className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-AR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Header */}
      <header className="glass fixed top-0 left-0 right-0 z-50 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md overflow-hidden">
              <img src="/veritel-logo.jpeg" alt="Veritel" className="w-full h-full" />
            </div>
            <span className="font-black text-xl text-white tracking-tight">Veritel</span>
          </div>

          <div className="flex items-center gap-4">
            <ThemeToggle />
            <Button
              data-testid="back-to-dashboard-btn"
              variant="ghost"
              onClick={() => navigate("/dashboard")}
              className="text-zinc-400 hover:text-white hover:bg-zinc-800"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>

            <div className="flex items-center gap-3 pl-4 border-l border-zinc-800">
              {user?.picture && (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-8 h-8 rounded-full"
                />
              )}
              <span className="text-sm text-zinc-300 hidden sm:block">{user?.name}</span>
              <Button
                data-testid="logout-btn"
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="text-zinc-400 hover:text-white hover:bg-zinc-800"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-2 flex items-center gap-3">
                <History className="w-8 h-8 text-violet-400" />
                Historial
              </h1>
              <p className="text-zinc-400">
                Todas tus verificaciones guardadas.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Button
                data-testid="refresh-btn"
                variant="outline"
                size="icon"
                onClick={() => {
                  fetchHistory();
                  fetchStats();
                }}
                className="border-zinc-700 bg-transparent hover:bg-zinc-900 text-zinc-300"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    data-testid="export-btn"
                    variant="outline"
                    className="border-zinc-700 bg-transparent hover:bg-zinc-900 text-zinc-300"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Exportar
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-zinc-900 border-zinc-800">
                  <DropdownMenuItem
                    data-testid="export-csv-btn"
                    onClick={() => handleExport("csv")}
                    className="text-zinc-300 hover:bg-zinc-800 hover:text-white cursor-pointer"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Exportar CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="export-xlsx-btn"
                    onClick={() => handleExport("xlsx")}
                    className="text-zinc-300 hover:bg-zinc-800 hover:text-white cursor-pointer"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Exportar Excel
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                data-testid="clear-history-btn"
                variant="outline"
                onClick={() => setClearDialogOpen(true)}
                className="border-rose-900 bg-rose-900/20 hover:bg-rose-900/40 text-rose-300"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Limpiar
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="p-4 rounded-lg bg-zinc-900/50 border border-white/10 backdrop-blur-sm">
                <p className="text-xs font-semibold tracking-widest uppercase text-zinc-500 mb-1">Total</p>
                <p className="text-2xl font-bold text-white">{stats.total_verifications}</p>
              </div>
              <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <p className="text-xs font-semibold tracking-widest uppercase text-emerald-400 mb-1">Válidos</p>
                <p className="text-2xl font-bold text-emerald-400">{stats.valid_count}</p>
              </div>
              <div className="p-4 rounded-lg bg-rose-500/10 border border-rose-500/20">
                <p className="text-xs font-semibold tracking-widest uppercase text-rose-400 mb-1">Inválidos</p>
                <p className="text-2xl font-bold text-rose-400">{stats.invalid_count}</p>
              </div>
              <div className="p-4 rounded-lg bg-zinc-900/50 border border-white/10 backdrop-blur-sm">
                <p className="text-xs font-semibold tracking-widest uppercase text-zinc-500 mb-1">Tasa Validez</p>
                <p className="text-2xl font-bold text-violet-400">{stats.valid_rate.toFixed(0)}%</p>
              </div>
            </div>
          )}

          {/* Filter */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-zinc-500" />
              <span className="text-sm text-zinc-400">Filtrar por tipo:</span>
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="filter-select" className="w-40 bg-zinc-950 border-zinc-800 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all" className="text-white hover:bg-zinc-800">Todos</SelectItem>
                <SelectItem value="phone" className="text-white hover:bg-zinc-800">Teléfono</SelectItem>
                <SelectItem value="email" className="text-white hover:bg-zinc-800">Email</SelectItem>
                <SelectItem value="combined" className="text-white hover:bg-zinc-800">Combinado</SelectItem>
                <SelectItem value="bulk" className="text-white hover:bg-zinc-800">Masivo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* History List */}
          <div className="bg-zinc-900/50 border border-white/10 rounded-lg backdrop-blur-sm">
            {loading ? (
              <div className="p-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-violet-400 mx-auto mb-4" />
                <p className="text-zinc-400">Cargando historial...</p>
              </div>
            ) : history.length === 0 ? (
              <div className="p-12 text-center">
                <History className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-400">No hay verificaciones en el historial</p>
              </div>
            ) : (
              <ScrollArea className="h-[600px]">
                <div className="divide-y divide-zinc-800">
                  {history.map((item) => (
                    <div
                      key={item.id}
                      className="p-4 hover:bg-zinc-900/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4 flex-1 min-w-0">
                          <div
                            className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${
                              item.is_valid
                                ? "bg-emerald-500/20 text-emerald-400"
                                : "bg-rose-500/20 text-rose-400"
                            }`}
                          >
                            {item.is_valid ? (
                              <ShieldCheck className="w-5 h-5" />
                            ) : (
                              <ShieldAlert className="w-5 h-5" />
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className="bg-zinc-800 text-zinc-400 border-zinc-700 text-xs capitalize flex items-center gap-1">
                                {getTypeIcon(item.input_type)}
                                {item.input_type}
                              </Badge>
                              <Badge
                                className={
                                  item.is_valid
                                    ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20"
                                    : "bg-rose-500/10 text-rose-500 border-rose-500/20"
                                }
                              >
                                {item.is_valid ? "Válido" : "Inválido"}
                              </Badge>
                            </div>

                            <p className="font-mono text-sm text-white truncate mb-1">
                              {item.input_data}
                            </p>

                            <div className="flex items-center gap-4 text-xs text-zinc-500">
                              <span>{formatDate(item.timestamp)}</span>
                              <span>Tipo: {item.type || "N/A"}</span>
                            </div>
                          </div>
                        </div>

                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(item.id)}
                          className="text-zinc-500 hover:text-rose-400 hover:bg-rose-500/10 shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </div>
      </main>

      {/* Clear Confirmation Dialog */}
      <AlertDialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">¿Eliminar todo el historial?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Esta acción no se puede deshacer. Se eliminarán todas las verificaciones guardadas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-clear-btn"
              onClick={handleClearAll}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              Eliminar Todo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default HistoryPage;
